import Swal from "sweetalert2";
import { getDirection, translate, translateMessage } from "./language_helper";

/**
 * Drop-in replacement for `import Swal from "sweetalert2"`.
 *
 * Every dialog that goes through here is localised before it is shown:
 *  - static labels (title, buttons, footer) are looked up as translation keys
 *  - free text (`text` / `html`), usually the message returned by the API,
 *    goes through translateMessage() and falls back to the original text when
 *    the app has no translation for it
 *  - the popup is flipped to RTL when Arabic is selected
 *
 * The sweetalert2 API is left untouched, so existing call sites keep working
 * exactly as before, in both the object form
 * `Swal.fire({ title, text, icon })` and the positional form
 * `Swal.fire("Deleted!", message, "success")`.
 */

// Options holding static UI labels -> translated as translation keys.
const LABEL_OPTIONS = [
  "title",
  "titleText",
  "confirmButtonText",
  "cancelButtonText",
  "denyButtonText",
  "closeButtonAriaLabel",
  "footer",
  "inputPlaceholder",
  "inputLabel",
];

// Options holding message text -> translated as API / free text.
const MESSAGE_OPTIONS = ["text", "html"];

const localizeOptions = (options) => {
  if (!options || typeof options !== "object") return options;

  const localized = { ...options };

  LABEL_OPTIONS.forEach((key) => {
    if (typeof localized[key] === "string" && localized[key].trim()) {
      localized[key] = translate(localized[key]);
    }
  });

  MESSAGE_OPTIONS.forEach((key) => {
    if (typeof localized[key] === "string" && localized[key].trim()) {
      localized[key] = translateMessage(localized[key]);
    }
  });

  // sweetalert2 renders "OK" / "Cancel" itself when no label is provided.
  if (localized.confirmButtonText === undefined) {
    localized.confirmButtonText = translate("OK");
  }
  if (localized.showCancelButton && localized.cancelButtonText === undefined) {
    localized.cancelButtonText = translate("Cancel");
  }

  if (localized.direction === undefined) {
    localized.direction = getDirection();
  }

  return localized;
};

/**
 * Positional signature: fire(title, html, icon).
 * `title` is a static label, the second argument is the message.
 */
const localizeArgs = (args) => {
  if (args.length === 0) return args;

  const [first, ...rest] = args;

  if (typeof first === "string") {
    const [message, icon] = rest;
    return [
      localizeOptions({
        title: first,
        ...(message !== undefined ? { text: message } : {}),
        ...(icon !== undefined ? { icon } : {}),
      }),
    ];
  }

  return [localizeOptions(first), ...rest];
};

const fire = (...args) => Swal.fire(...localizeArgs(args));

const mixin = (options) => {
  const instance = Swal.mixin(localizeOptions(options));
  return new Proxy(instance, {
    get(target, prop) {
      if (prop === "fire") {
        return (...args) => target.fire(...localizeArgs(args));
      }
      const value = Reflect.get(target, prop);
      return typeof value === "function" ? value.bind(target) : value;
    },
  });
};

/**
 * Proxy so the whole sweetalert2 surface (close, showLoading, isVisible,
 * update, getPopup, DismissReason, ...) keeps working unchanged, with `this`
 * still bound to the real Swal.
 */
const LocalizedSwal = new Proxy(Swal, {
  get(target, prop) {
    if (prop === "fire") return fire;
    if (prop === "mixin") return mixin;
    const value = Reflect.get(target, prop);
    return typeof value === "function" ? value.bind(target) : value;
  },
});

export default LocalizedSwal;

/** Convenience wrappers for new code. */
export const showSuccess = (message, options = {}) =>
  fire({ title: "Success", text: message, icon: "success", ...options });

export const showError = (message, options = {}) =>
  fire({
    title: "Error",
    text: message || "Something went wrong",
    icon: "error",
    ...options,
  });

/** Small auto-dismissing corner notification (snackbar-style), no confirm button. */
export const showToast = (message, options = {}) =>
  fire({
    text: message,
    icon: "success",
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    ...options,
  });

export const showDeleteConfirm = (options = {}) =>
  fire({
    title: "Are you sure?",
    text: "You won't be able to revert this!",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#f46a6a",
    confirmButtonText: "Yes, delete it!",
    cancelButtonText: "Cancel",
    ...options,
  });
